import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Brand, StaffBadge } from "@/components/Brand";
import { OneSignalInit, enableOrderAlerts } from "@/components/OneSignalInit";
import { StatusBadge, PaymentBadge } from "@/components/StatusBadge";
import { STATUS_LABELS, flowFor } from "@/lib/statuses";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Home as HomeIcon, UtensilsCrossed, ShoppingCart, MapPin, Zap, Moon, Bike, Info, Phone,
  Plus, Minus, Trash2, Lock, Camera, Instagram, Copy, Search, ChevronLeft, CheckCircle2,
  ArrowRight, X, Clock,
} from "lucide-react";
import { toast } from "sonner";

const FLAVORS = ["Plain", "Lemon Pepper", "Garlic Parm", "Honey BBQ", "Hot", "Mild", "Teriyaki", "Spicy Garlic", "ZA! Sauce (Signature)"];
const SIDES = ["French Fries", "Cheese Fries", "Loaded Fries", "Extra Sauce"];
const DRINKS = ["Soda", "Lemonade", "Fruit Punch", "Blue Razz", "Mango"];

const NAV = [
  { key: "home", label: "Home", icon: HomeIcon },
  { key: "menu", label: "Menu", icon: UtensilsCrossed },
  { key: "cart", label: "Cart", icon: ShoppingCart },
  { key: "track", label: "Track", icon: MapPin },
];

export default function Customer() {
  const [access, setAccess] = useState(() => localStorage.getItem("zr_access") === "1");
  const [tab, setTab] = useState("home");
  const [menu, setMenu] = useState([]);
  const [settings, setSettings] = useState({});
  const { count } = useCart();

  const loadMenu = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([api.get("/menu"), api.get("/public/settings")]);
      setMenu(m.data); setSettings(s.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (access) loadMenu(); }, [access, loadMenu]);

  if (!access) return <AccessGate onOk={() => { setAccess(true); localStorage.setItem("zr_access", "1"); }} />;

  return (
    <div className="min-h-screen zr-grid-bg flex justify-center">
      <div className="relative w-full max-w-md bg-[#0A0B0E] min-h-screen flex flex-col shadow-[0_0_60px_rgba(204,255,0,0.08)] border-x border-zinc-900">
        <OneSignalInit />
        <header className="sticky top-0 z-20 flex items-center justify-between px-5 py-4 bg-[#0A0B0E]/90 backdrop-blur-xl border-b border-zinc-900">
          <button onClick={() => setTab("home")}><Brand /></button>
          <StaffBadge />
        </header>
        <main className="flex-1 overflow-y-auto pb-24">
          {tab === "home" && <HomeTab go={setTab} settings={settings} />}
          {tab === "menu" && <MenuTab menu={menu} go={setTab} />}
          {tab === "cart" && <CartFlow menu={menu} settings={settings} go={setTab} />}
          {tab === "track" && <TrackTab settings={settings} menu={menu} go={setTab} />}
          {tab === "about" && <AboutTab settings={settings} />}
          {tab === "contact" && <ContactTab settings={settings} />}
        </main>
        <nav className="fixed bottom-0 z-30 w-full max-w-md border-t border-zinc-800 bg-[#0A0B0E]/95 backdrop-blur-xl">
          <div className="grid grid-cols-4">
            {NAV.map((n) => {
              const Icon = n.icon; const active = tab === n.key;
              return (
                <button key={n.key} data-testid={`nav-${n.key}-tab`} onClick={() => setTab(n.key)}
                  className={`relative flex flex-col items-center gap-1 py-3 ${active ? "text-[#CCFF00]" : "text-zinc-500 hover:text-zinc-300"}`}>
                  <Icon className="h-5 w-5" />
                  <span className="font-heading text-[11px] font-bold uppercase tracking-wide">{n.label}</span>
                  {n.key === "cart" && count > 0 && (
                    <span className="absolute right-6 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[10px] font-bold text-white">{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}

function AccessGate({ onOk }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/access/verify", { code });
      localStorage.setItem("zr_code", code);
      toast.success("Access granted");
      onOk();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen zr-grid-bg flex flex-col items-center justify-center px-6">
      <Brand size="lg" className="mb-2" />
      <p className="font-heading text-[#CCFF00] uppercase tracking-[0.3em] text-xs mb-8">We Pull Up. You Relax.</p>
      <div className="w-full max-w-sm rounded-2xl border-2 border-[#CCFF00]/50 bg-[#12141C]/90 p-7 shadow-[0_0_30px_rgba(204,255,0,0.15)] backdrop-blur-xl">
        <div className="flex justify-center"><span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#CCFF00]/15"><Lock className="h-6 w-6 text-[#CCFF00]" /></span></div>
        <h1 className="mt-4 text-center font-heading text-2xl font-extrabold uppercase text-white">Private Access Code</h1>
        <p className="mt-1 text-center text-sm text-zinc-400">Ask ZA!RUNNERzz for the private code.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <Input data-testid="access-code-input" value={code} onChange={(e) => setCode(e.target.value)} required
            placeholder="ENTER CODE" className="bg-zinc-900 border-zinc-700 text-white text-center font-mono text-lg uppercase tracking-widest" />
          <Button data-testid="access-submit-btn" type="submit" disabled={busy}
            className="w-full h-12 rounded-full bg-[#CCFF00] text-black font-heading font-bold uppercase tracking-wider hover:bg-[#d4ff33] active:scale-95 transition-transform">
            {busy ? "Checking..." : "Enter"}
          </Button>
        </form>
      </div>
      <Link to="/login" className="mt-6 text-xs text-zinc-500 hover:text-zinc-300">Staff sign in →</Link>
    </div>
  );
}

function HomeTab({ go, settings }) {
  return (
    <div className="px-5 py-6 space-y-6">
      {settings.accepting_orders === false && (
        <div className="rounded-xl border border-[#FF3B30]/50 bg-[#FF3B30]/10 p-4 text-center text-sm text-[#FF3B30] font-semibold" data-testid="closed-banner">
          ZA!RUNNERzz is currently closed for new orders. Check back soon.
        </div>
      )}
      <div className="rounded-2xl border-2 border-[#CCFF00]/60 bg-gradient-to-b from-[#141610] to-[#0A0B0E] p-6 shadow-[0_0_30px_rgba(204,255,0,0.15)]">
        <img src="/brand/hero.jpg" alt="ZA!RUNNERzz — We Pull Up. You Relax." className="w-full h-auto" />
        <p className="mt-4 text-sm text-zinc-300 leading-relaxed">Late-night wings, tenders, fries & drinks — hot to your door or ready for pickup.</p>
        <Button data-testid="order-now-btn" onClick={() => go("menu")}
          className="mt-5 w-full h-12 rounded-full bg-[#CCFF00] text-black font-heading font-bold uppercase tracking-wider text-base hover:bg-[#d4ff33] active:scale-95 transition-transform shadow-[0_0_20px_rgba(204,255,0,0.4)]">Order Now</Button>
        <Button data-testid="view-menu-btn" onClick={() => go("menu")} variant="outline"
          className="mt-3 w-full h-11 rounded-full border-[#CCFF00]/50 bg-transparent text-[#CCFF00] font-heading font-bold uppercase tracking-wider hover:bg-[#CCFF00]/10 hover:text-[#CCFF00]">View Menu</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <Pill icon={Zap} label="Fast Delivery" /><Pill icon={Moon} label="Late Night" /><Pill icon={Bike} label="Pickup & Delivery" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <TileBtn testid="tile-track" icon={MapPin} title="Track" sub="Enter your order #" onClick={() => go("track")} />
        <TileBtn testid="tile-about" icon={Info} title="About" sub="Who we are" onClick={() => go("about")} red />
        <TileBtn testid="tile-contact" icon={Phone} title="Contact" sub="Reach us" onClick={() => go("contact")} />
      </div>
      <p className="text-center text-xs text-zinc-500 leading-relaxed">{settings.out_of_town_note}</p>
    </div>
  );
}

function Pill({ icon: Icon, label }) {
  return <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900/70 px-3 py-1.5 text-xs font-heading font-bold uppercase tracking-wide text-zinc-200"><Icon className="h-3.5 w-3.5 text-[#CCFF00]" /> {label}</span>;
}
function TileBtn({ testid, icon: Icon, title, sub, onClick, red }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`rounded-xl border p-4 text-left transition-colors ${red ? "border-[#FF3B30]/40 hover:border-[#FF3B30]" : "border-zinc-800 hover:border-zinc-600"} bg-zinc-900/70`}>
      <Icon className={`h-5 w-5 ${red ? "text-[#FF3B30]" : "text-[#CCFF00]"}`} />
      <p className="mt-3 font-heading font-bold uppercase text-white text-sm">{title}</p>
      <p className="text-[11px] text-zinc-500">{sub}</p>
    </button>
  );
}

function MenuTab({ menu, go }) {
  const { add, orderType } = useCart();
  return (
    <div className="px-5 py-6 space-y-4">
      <h2 className="font-heading text-3xl font-extrabold uppercase text-white">The Menu</h2>
      <p className="text-xs text-zinc-500">Showing <span className="text-[#CCFF00] font-mono uppercase">{orderType}</span> prices — change in cart.</p>
      <div className="space-y-4">
        {menu.map((m) => {
          const price = orderType === "delivery" ? m.delivery_price : m.pickup_price;
          const sold = !m.available;
          return (
            <div key={m.key} data-testid={`meal-${m.key}`} className={`overflow-hidden rounded-2xl border bg-black ${sold ? "border-zinc-800 opacity-70" : "border-zinc-800 hover:border-[#CCFF00]/40"} transition-colors`}>
              <div className="relative">
                <img src={m.image_url} alt={m.name} className="block w-full h-auto" />
                {sold && <div className="absolute inset-0 flex items-center justify-center bg-black/60"><span className="font-heading text-3xl font-black uppercase text-[#FF3B30] border-2 border-[#FF3B30] px-5 py-1.5 rotate-[-8deg]">Sold Out</span></div>}
              </div>
              <div className="flex items-center justify-between gap-3 bg-zinc-900/70 p-4">
                <div>
                  <h3 className="font-heading text-lg font-bold uppercase text-white">{m.name}</h3>
                  <p className="text-xs text-zinc-400"><span className="uppercase">{orderType}</span> price <span className="font-mono text-[#CCFF00]">${price}</span></p>
                </div>
                <Button data-testid={`add-${m.key}-btn`} disabled={sold} onClick={() => { add(m.key); toast.success(`${m.name} added`); }}
                  className="shrink-0 rounded-full bg-[#CCFF00] text-black font-bold hover:bg-[#d4ff33] active:scale-95 transition-transform disabled:opacity-40">
                  {sold ? "Sold Out" : <><Plus className="h-4 w-4 mr-1" /> Add</>}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CartFlow({ menu, settings, go }) {
  const { items, setQty, remove, clear, orderType, setOrderType, setMeta } = useCart();
  const [step, setStep] = useState("cart"); // cart, info, recognition, payment, review, done
  const [info, setInfo] = useState({ full_name: "", phone: "", instructions: "" });
  const [addr, setAddr] = useState({ street: "", apt: "", city: "", state: "CT", zip: "", instructions: "" });
  const [rec, setRec] = useState({ type: "", instagram: "", photo_path: "" });
  const [photoPreview, setPhotoPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [payment, setPayment] = useState("cash");
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null);

  const mealMap = Object.fromEntries(menu.map((m) => [m.key, m]));
  const priceOf = (key) => { const m = mealMap[key]; return m ? (orderType === "delivery" ? m.delivery_price : m.pickup_price) : 0; };
  const total = items.reduce((s, i) => s + priceOf(i.key) * i.qty, 0);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/recognition", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRec({ type: "photo", instagram: "", photo_path: data.path });
      setPhotoPreview(URL.createObjectURL(file));
      toast.success("Photo attached");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const placeOrder = async () => {
    setBusy(true);
    try {
      const body = {
        access_code: localStorage.getItem("zr_code") || "",
        order_type: orderType,
        items: items.map((i) => ({ key: i.key, qty: i.qty, flavor: i.flavor || "", extras: i.extras || [] })),
        full_name: info.full_name, phone: info.phone, instructions: info.instructions,
        delivery_address: orderType === "delivery" ? addr : null,
        recognition: rec, payment_method: payment,
      };
      const { data } = await api.post("/orders", body);
      setPlaced(data); clear(); setStep("done");
      toast.success(`Order ${data.order_number} placed!`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ----- DONE / CONFIRMATION -----
  if (step === "done" && placed) return <Confirmation placed={placed} settings={settings} go={go} />;

  // ----- CART -----
  if (step === "cart") {
    return (
      <div className="px-5 py-6 space-y-5">
        <h2 className="font-heading text-3xl font-extrabold uppercase text-white">Your Cart</h2>
        {!items.length ? (
          <Empty go={go} />
        ) : (
          <>
            <div className="space-y-3">
              {items.map((i) => {
                const m = mealMap[i.key];
                const extras = i.extras || [];
                const toggle = (x) => setMeta(i.key, { extras: extras.includes(x) ? extras.filter((e) => e !== x) : [...extras, x] });
                return (
                  <div key={i.key} data-testid={`cart-item-${i.key}`} className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3">
                    <div className="flex items-center justify-between">
                      <div><p className="font-semibold text-white text-sm">{m?.name || i.key}</p><p className="font-mono text-xs text-[#CCFF00]">${(priceOf(i.key) * i.qty).toFixed(2)}</p></div>
                      <div className="flex items-center gap-2">
                        <button data-testid={`dec-${i.key}-btn`} onClick={() => setQty(i.key, i.qty - 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-zinc-700 text-zinc-300 hover:text-white"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-5 text-center font-mono text-white">{i.qty}</span>
                        <button data-testid={`inc-${i.key}-btn`} onClick={() => setQty(i.key, i.qty + 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-zinc-700 text-zinc-300 hover:text-white"><Plus className="h-3.5 w-3.5" /></button>
                        <button data-testid={`remove-${i.key}-btn`} onClick={() => remove(i.key)} className="ml-1 text-zinc-500 hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-zinc-500">Flavor (optional)</p>
                        <select data-testid={`flavor-${i.key}`} value={i.flavor || ""} onChange={(e) => setMeta(i.key, { flavor: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm text-white">
                          <option value="">No preference</option>
                          {FLAVORS.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-zinc-500">Sides <span className="text-zinc-600">(no charge)</span></p>
                        <div className="mt-1 flex flex-wrap gap-1.5">{SIDES.map((x) => { const on = extras.includes(x); return <button key={x} data-testid={`extra-${i.key}-${x}`} onClick={() => toggle(x)} className={`rounded-full border px-2.5 py-1 text-xs ${on ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : "border-zinc-700 text-zinc-300 hover:text-white"}`}>{x}</button>; })}</div>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-zinc-500">Drinks <span className="text-zinc-600">(no charge)</span></p>
                        <div className="mt-1 flex flex-wrap gap-1.5">{DRINKS.map((x) => { const on = extras.includes(x); return <button key={x} data-testid={`extra-${i.key}-${x}`} onClick={() => toggle(x)} className={`rounded-full border px-2.5 py-1 text-xs ${on ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : "border-zinc-700 text-zinc-300 hover:text-white"}`}>{x}</button>; })}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {["delivery", "pickup"].map((t) => (
                <button key={t} data-testid={`ordertype-${t}-btn`} onClick={() => setOrderType(t)}
                  className={`rounded-lg border py-2.5 font-heading font-bold uppercase text-sm ${orderType === t ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>{t}</button>
              ))}
            </div>
            <Totals total={total} />
            <Button data-testid="continue-shopping-btn" variant="outline" onClick={() => go("menu")} className="w-full border-zinc-700 bg-transparent text-zinc-300 hover:text-white">Continue Shopping</Button>
            <Button data-testid="checkout-btn" onClick={() => setStep("info")}
              className="w-full h-12 rounded-full bg-[#CCFF00] text-black font-heading font-bold uppercase tracking-wider hover:bg-[#d4ff33] active:scale-95 transition-transform">Checkout →</Button>
          </>
        )}
      </div>
    );
  }

  // ----- INFO -----
  if (step === "info") {
    const validDelivery = orderType !== "delivery" || (addr.street && addr.city && addr.zip);
    const valid = info.full_name && info.phone && validDelivery;
    return (
      <StepWrap title="Your Info" onBack={() => setStep("cart")}>
        {orderType === "pickup" && (
          <div className="rounded-lg border border-[#CCFF00]/30 bg-[#CCFF00]/5 p-3 text-xs text-zinc-300">
            <b className="text-[#CCFF00]">PICKUP NOTICE:</b> After your order is placed and confirmed, the pickup address will be sent to the phone number entered with your order.
          </div>
        )}
        <Field label="Full Name"><Input data-testid="info-name-input" value={info.full_name} onChange={(e) => setInfo({ ...info, full_name: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" /></Field>
        <Field label="Phone"><Input data-testid="info-phone-input" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" /></Field>
        <Field label="Order Instructions (optional)"><Textarea data-testid="info-instructions-input" value={info.instructions} onChange={(e) => setInfo({ ...info, instructions: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" /></Field>
        {orderType === "delivery" && (
          <>
            <div className="rounded-lg border border-zinc-700 bg-zinc-900/50 p-3 text-xs text-zinc-400">{settings.out_of_town_note}</div>
            <Field label="Street Address"><Input data-testid="addr-street-input" value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Apt/Unit (optional)"><Input data-testid="addr-apt-input" value={addr.apt} onChange={(e) => setAddr({ ...addr, apt: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" /></Field>
              <Field label="City"><Input data-testid="addr-city-input" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="State"><Input data-testid="addr-state-input" value={addr.state} disabled className="bg-zinc-900 border-zinc-700 text-white font-mono" /></Field>
              <Field label="ZIP"><Input data-testid="addr-zip-input" value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" /></Field>
            </div>
            <Field label="Delivery Instructions (optional)"><Textarea data-testid="addr-instructions-input" value={addr.instructions} onChange={(e) => setAddr({ ...addr, instructions: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" /></Field>
          </>
        )}
        <Button data-testid="to-recognition-btn" disabled={!valid} onClick={() => setStep("recognition")}
          className="w-full h-12 rounded-full bg-[#CCFF00] text-black font-heading font-bold uppercase tracking-wider hover:bg-[#d4ff33] disabled:opacity-40">Continue →</Button>
      </StepWrap>
    );
  }

  // ----- RECOGNITION -----
  if (step === "recognition") {
    const valid = (rec.type === "photo" && rec.photo_path) || (rec.type === "instagram" && rec.instagram.trim());
    return (
      <StepWrap title="Recognition" onBack={() => setStep("info")}>
        <p className="text-sm text-zinc-400">For a safe handoff, provide ONE: a recent photo OR your exact Instagram username. Used by our team for human recognition only — no facial recognition.</p>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="rec-photo-tab" onClick={() => setRec({ ...rec, type: "photo" })} className={`flex items-center justify-center gap-2 rounded-lg border py-3 font-heading font-bold uppercase text-sm ${rec.type === "photo" ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : "border-zinc-700 text-zinc-400"}`}><Camera className="h-4 w-4" /> Photo</button>
          <button data-testid="rec-ig-tab" onClick={() => setRec({ ...rec, type: "instagram" })} className={`flex items-center justify-center gap-2 rounded-lg border py-3 font-heading font-bold uppercase text-sm ${rec.type === "instagram" ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : "border-zinc-700 text-zinc-400"}`}><Instagram className="h-4 w-4" /> Instagram</button>
        </div>
        {rec.type === "photo" && (
          <div className="rounded-xl border border-zinc-700 bg-zinc-900/50 p-4 text-center">
            {photoPreview ? <img src={photoPreview} alt="preview" className="mx-auto mb-3 h-40 w-40 rounded-lg object-cover" /> : <Camera className="mx-auto mb-3 h-10 w-10 text-zinc-600" />}
            <input data-testid="rec-photo-input" id="photo" type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            <label htmlFor="photo" className="inline-block cursor-pointer rounded-full bg-[#CCFF00] px-5 py-2 text-sm font-bold text-black hover:bg-[#d4ff33]">{uploading ? "Uploading..." : photoPreview ? "Change Photo" : "Take / Upload Photo"}</label>
          </div>
        )}
        {rec.type === "instagram" && (
          <Field label="Instagram Username"><Input data-testid="rec-ig-input" value={rec.instagram} onChange={(e) => setRec({ ...rec, instagram: e.target.value })} placeholder="@username or username" className="bg-zinc-900 border-zinc-700 text-white font-mono" /></Field>
        )}
        <Button data-testid="to-payment-btn" disabled={!valid} onClick={() => setStep("payment")}
          className="w-full h-12 rounded-full bg-[#CCFF00] text-black font-heading font-bold uppercase tracking-wider hover:bg-[#d4ff33] disabled:opacity-40">Continue →</Button>
      </StepWrap>
    );
  }

  // ----- PAYMENT -----
  if (step === "payment") {
    return (
      <StepWrap title="Payment" onBack={() => setStep("recognition")}>
        <div className="grid grid-cols-2 gap-2">
          {[{ k: "cash", l: "Cash" }, { k: "cashapp", l: "Cash App" }].map((p) => (
            <button key={p.k} data-testid={`payment-${p.k}-btn`} onClick={() => setPayment(p.k)}
              className={`rounded-lg border py-3 font-heading font-bold uppercase text-sm ${payment === p.k ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-zinc-700 text-zinc-400"}`}>{p.l}</button>
          ))}
        </div>
        {payment === "cashapp" && <div className="rounded-lg border border-emerald-500/40 bg-emerald-950/30 p-3 text-sm text-zinc-300">You'll send <span className="font-mono font-bold text-white">${total.toFixed(2)}</span> to <span className="font-mono text-emerald-300">{settings.cashapp_cashtag}</span> after placing. Then tap <b>I SENT PAYMENT</b>. Admin verifies manually.</div>}
        {payment === "cash" && <div className="rounded-lg border border-lime-500/40 bg-lime-950/20 p-3 text-sm text-zinc-300">Cash due at {orderType === "delivery" ? "delivery" : "pickup"}.</div>}
        <Button data-testid="to-review-btn" onClick={() => setStep("review")} className="w-full h-12 rounded-full bg-[#CCFF00] text-black font-heading font-bold uppercase tracking-wider hover:bg-[#d4ff33]">Review Order →</Button>
      </StepWrap>
    );
  }

  // ----- REVIEW -----
  return (
    <StepWrap title="Review & Place" onBack={() => setStep("payment")}>
      <div className="space-y-2 rounded-xl border border-zinc-800 bg-zinc-900/70 p-4 text-sm">
        {items.map((i) => <div key={i.key} className="flex justify-between text-zinc-300"><span>{i.qty}× {mealMap[i.key]?.name}</span><span className="font-mono">${(priceOf(i.key) * i.qty).toFixed(2)}</span></div>)}
        <div className="flex justify-between border-t border-zinc-800 pt-2 font-bold text-white"><span>Total</span><span className="font-mono text-[#CCFF00]">${total.toFixed(2)}</span></div>
      </div>
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-4 text-sm text-zinc-300 space-y-1">
        <p><b className="text-zinc-500">Type:</b> {orderType}</p>
        <p><b className="text-zinc-500">Name:</b> {info.full_name} · {info.phone}</p>
        {orderType === "delivery" && <p><b className="text-zinc-500">Address:</b> {addr.street}{addr.apt ? `, ${addr.apt}` : ""}, {addr.city}, {addr.state} {addr.zip}</p>}
        <p><b className="text-zinc-500">Recognition:</b> {rec.type === "photo" ? "Photo attached" : `IG @${rec.instagram.replace(/^@/, "")}`}</p>
        <p><b className="text-zinc-500">Payment:</b> {payment === "cashapp" ? "Cash App" : "Cash"}</p>
      </div>
      <Button data-testid="place-order-btn" onClick={placeOrder} disabled={busy}
        className="w-full h-12 rounded-full bg-[#CCFF00] text-black font-heading font-bold uppercase tracking-wider hover:bg-[#d4ff33] active:scale-95 transition-transform shadow-[0_0_20px_rgba(204,255,0,0.4)]">
        {busy ? "Placing..." : `Place Order · $${total.toFixed(2)}`}
      </Button>
    </StepWrap>
  );
}

function Confirmation({ placed, settings, go }) {
  const [order, setOrder] = useState(placed);
  const sentPayment = async () => {
    try { const { data } = await api.post(`/orders/${order.order_number}/sent-payment`); setOrder(data); toast.success("Marked as sent — awaiting verification"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="px-5 py-6 space-y-5">
      <div className="rounded-xl border border-[#CCFF00]/40 bg-zinc-900/70 p-6 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#CCFF00]/15"><CheckCircle2 className="h-6 w-6 text-[#CCFF00]" /></div>
        <p className="font-heading text-2xl font-extrabold uppercase text-white">Order Placed!</p>
        <p className="mt-1 font-mono text-2xl text-[#CCFF00]" data-testid="placed-order-number">{order.order_number}</p>
        <p className="mt-1 text-sm text-zinc-400">Total ${order.total.toFixed(2)} · {order.order_type}</p>
      </div>

      <div className="rounded-xl border-2 border-[#CCFF00]/50 bg-[#141610] p-5 text-center">
        <p className="font-heading uppercase text-sm text-zinc-400">Your 4-Digit Verification Code</p>
        <p className="mt-1 font-mono text-5xl font-black tracking-[0.3em] text-[#CCFF00]" data-testid="verification-code">{order.verification_code}</p>
        <p className="mt-2 text-xs text-zinc-500">Show this code to your driver or pickup staff. Do not share it before then.</p>
      </div>

      {order.payment_method === "cashapp" && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/30 p-5">
          <p className="font-heading font-bold uppercase text-emerald-300">Cash App Payment</p>
          <p className="mt-2 text-sm text-zinc-300">Send <span className="font-mono font-bold text-white">${order.total.toFixed(2)}</span> to:</p>
          <div className="mt-2 flex items-center justify-between rounded-lg bg-black/40 px-3 py-2">
            <span className="font-mono text-lg text-emerald-300" data-testid="cashtag">{settings.cashapp_cashtag}</span>
            <button onClick={() => { navigator.clipboard.writeText(settings.cashapp_cashtag); toast.success("Copied"); }} className="text-zinc-400 hover:text-white"><Copy className="h-4 w-4" /></button>
          </div>
          {order.payment_status === "awaiting_verification" ? (
            <p className="mt-3 text-sm text-pink-300">Awaiting payment verification by admin.</p>
          ) : (
            <Button data-testid="i-sent-payment-btn" onClick={sentPayment} className="mt-3 w-full bg-emerald-600 text-white font-bold hover:bg-emerald-500">I SENT PAYMENT</Button>
          )}
        </div>
      )}

      {order.order_type === "pickup" && <div className="rounded-lg border border-[#CCFF00]/30 bg-[#CCFF00]/5 p-3 text-xs text-zinc-300">Pickup location will be sent to your phone after confirmation.</div>}

      <Button data-testid="track-now-btn" onClick={() => { localStorage.setItem("zr_last_order", order.order_number); go("track"); }}
        className="w-full bg-[#CCFF00] text-black font-bold hover:bg-[#d4ff33]">Track My Order</Button>
    </div>
  );
}

function TrackTab({ settings, menu, go }) {
  const { replace } = useCart();
  const [q, setQ] = useState(localStorage.getItem("zr_last_order") || "");
  const [order, setOrder] = useState(null);
  const [showCode, setShowCode] = useState(false);

  const doTrack = useCallback(async (num) => {
    const on = (num || q).trim().toUpperCase();
    if (!on) return;
    try { const { data } = await api.get(`/orders/track/${on}`); setOrder(data); }
    catch (e) { setOrder(null); toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Order not found"); }
  }, [q]);

  useEffect(() => { if (localStorage.getItem("zr_last_order")) doTrack(localStorage.getItem("zr_last_order")); }, []); // eslint-disable-line

  useEffect(() => {
    if (!order) return undefined;
    if (["delivered", "order_picked_up", "cancelled"].includes(order.status)) return undefined;
    const on = order.order_number;
    const id = setInterval(async () => {
      try { const { data } = await api.get(`/orders/track/${on}`); setOrder(data); } catch { /* keep last known */ }
    }, 5000);
    return () => clearInterval(id);
  }, [order?.order_number, order?.status]); // eslint-disable-line

  const action = async (fn) => { try { const { data } = await fn(); setOrder(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const orderAgain = () => {
    const available = new Set(menu.filter((m) => m.available).map((m) => m.key));
    const rebuilt = (order.items || []).filter((i) => available.has(i.key)).map((i) => ({ key: i.key, qty: i.qty }));
    if (!rebuilt.length) { toast.error("Items no longer available"); return; }
    replace(rebuilt, order.order_type); toast.success("Cart rebuilt with current prices"); go("cart");
  };

  const flow = order ? flowFor(order.order_type) : [];
  const terminal = order && ["delivered", "order_picked_up", "cancelled"].includes(order.status);

  return (
    <div className="px-5 py-6 space-y-5">
      <h2 className="font-heading text-3xl font-extrabold uppercase text-white">Track Order</h2>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="track-input" placeholder="ZA-######" className="bg-zinc-900 border-zinc-700 text-white uppercase font-mono" />
        <Button data-testid="track-btn" onClick={() => doTrack()} className="bg-[#CCFF00] text-black font-bold hover:bg-[#d4ff33] shrink-0"><Search className="h-4 w-4" /></Button>
      </div>

      {order && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-5 space-y-4" data-testid="track-result">
          <div className="flex items-center justify-between"><span className="font-mono text-[#CCFF00] text-lg">{order.order_number}</span><StatusBadge status={order.status} /></div>
          <div className="flex flex-wrap gap-2"><PaymentBadge status={order.payment_status} /></div>

          {["delivered", "order_picked_up"].includes(order.status) && (
            <div data-testid="order-complete-banner" className="rounded-lg border border-emerald-500/50 bg-emerald-950/40 p-3 text-center font-heading font-bold uppercase text-emerald-300">
              {order.order_type === "pickup" ? "Order Picked Up — Complete" : "Delivered — Order Complete"}
            </div>
          )}

          {order.eta && (order.eta.label || order.eta.message) && (
            <div className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-950/20 p-3 text-sm">
              <p className="flex items-center gap-1.5 text-fuchsia-300 font-semibold"><Clock className="h-4 w-4" /> ETA: {order.eta.label}{order.eta.minutes != null && order.eta.label !== order.eta.minutes + " minutes" ? "" : ""}</p>
              {(order.eta.window_start || order.eta.window_end) && <p className="text-xs text-zinc-400">Window: {order.eta.window_start} – {order.eta.window_end}</p>}
              {order.eta.message && <p className="mt-1 text-zinc-300">{order.eta.message}</p>}
            </div>
          )}
          {order.status === "arriving_soon" && <div className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-950/20 p-3 text-sm text-fuchsia-200">Your driver is arriving soon. Have your 4-digit verification code ready.</div>}

          <div className="space-y-2">
            {flow.map((s) => { const done = flow.indexOf(s) <= flow.indexOf(order.status); return (
              <div key={s} className="flex items-center gap-3"><span className={`h-2.5 w-2.5 rounded-full ${done ? "bg-[#CCFF00]" : "bg-zinc-700"}`} /><span className={`text-sm font-heading uppercase ${done ? "text-white" : "text-zinc-500"}`}>{STATUS_LABELS[s]}</span></div>
            ); })}
          </div>

          {order.driver_name && <p className="text-xs text-zinc-400">Runner: <span className="text-white">{order.driver_name}</span></p>}

          {!terminal && (
            <Button data-testid="show-code-btn" onClick={() => setShowCode(true)} className="w-full bg-[#CCFF00] text-black font-bold hover:bg-[#d4ff33]">Show My Verification Code</Button>
          )}

          {!terminal && (
            <Button data-testid="enable-alerts-btn" variant="outline" onClick={() => { enableOrderAlerts(order.order_number); toast.success("Allow notifications to get order alerts"); }} className="w-full border-zinc-700 bg-transparent text-zinc-300 hover:text-white">Turn On Order Alerts</Button>
          )}

          {order.payment_method === "cashapp" && order.payment_status === "unpaid" && (
            <Button data-testid="track-sent-payment-btn" onClick={() => action(() => api.post(`/orders/${order.order_number}/sent-payment`))} className="w-full bg-emerald-600 text-white font-bold hover:bg-emerald-500">I SENT PAYMENT (${order.total.toFixed(2)} to {settings.cashapp_cashtag})</Button>
          )}

          {order.order_type === "pickup" && !terminal && (
            <div className="space-y-2">
              {order.pickup_address_sent ? <p className="text-xs text-[#CCFF00]">Pickup address was sent to your phone.</p> : <p className="text-xs text-zinc-500">Pickup address will be sent to your phone after confirmation.</p>}
              <p className="font-heading text-xs uppercase text-zinc-400">On my way in:</p>
              <div className="flex flex-wrap gap-2">
                {[5, 10, 15, 20, 30].map((mi) => <button key={mi} data-testid={`onway-${mi}-btn`} onClick={() => action(() => api.post(`/orders/${order.order_number}/on-my-way`, { eta_minutes: mi }))} className="rounded-full border border-zinc-700 px-3 py-1.5 text-xs font-bold text-zinc-200 hover:border-[#CCFF00] hover:text-[#CCFF00]">{mi} min</button>)}
              </div>
              <Button data-testid="im-here-btn" onClick={() => action(() => api.post(`/orders/${order.order_number}/arrived`))} className="w-full bg-[#FF3B30] text-white font-bold hover:bg-[#ff5347]">I'M AT THE PICKUP LOCATION</Button>
              {order.customer_arrived && <p className="rounded-lg bg-zinc-800/60 p-2 text-xs text-zinc-300">You're checked in! Please wait for your order. ZA!RUNNERzz has been notified that you arrived. Have your 4-digit verification code ready.</p>}
            </div>
          )}

          {terminal && <Button data-testid="order-again-btn" onClick={orderAgain} className="w-full bg-[#CCFF00] text-black font-bold hover:bg-[#d4ff33]">Order Again</Button>}
        </div>
      )}

      {showCode && order && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0A0B0E] px-6" data-testid="code-overlay">
          <button onClick={() => setShowCode(false)} className="absolute top-6 right-6 text-zinc-500 hover:text-white"><X className="h-7 w-7" /></button>
          <p className="font-heading uppercase tracking-widest text-zinc-400">Verification Code</p>
          <p className="my-6 font-mono text-7xl font-black tracking-[0.2em] text-[#CCFF00]" data-testid="big-code">{order.verification_code}</p>
          <p className="text-center text-zinc-400 max-w-xs">Show this code to your driver or pickup staff.</p>
          <Button data-testid="copy-code-btn" onClick={() => { navigator.clipboard.writeText(order.verification_code); toast.success("Code copied"); }} className="mt-6 bg-[#CCFF00] text-black font-bold hover:bg-[#d4ff33]"><Copy className="h-4 w-4 mr-2" /> Copy Code</Button>
        </div>
      )}
    </div>
  );
}

function AboutTab({ settings }) {
  return (
    <div className="px-5 py-6 space-y-4">
      <h2 className="font-heading text-3xl font-extrabold uppercase text-white">About Us</h2>
      <img src="/brand/logo.jpg" alt="ZA!RUNNERzz" className="w-full h-auto rounded-xl border border-zinc-800" />
      <p className="text-sm text-zinc-300 leading-relaxed">{settings.about_text}</p>
      {settings.business_hours && <p className="text-sm text-zinc-400"><b className="text-[#CCFF00]">Hours:</b> {settings.business_hours}</p>}
    </div>
  );
}

function ContactTab({ settings }) {
  const rows = [
    { icon: Phone, label: "Phone", value: settings.business_phone },
    { icon: Instagram, label: "Instagram", value: settings.instagram },
    { icon: Info, label: "Snapchat", value: settings.snapchat },
    { icon: Info, label: "Email", value: settings.email },
  ].filter((r) => r.value);
  return (
    <div className="px-5 py-6 space-y-4">
      <h2 className="font-heading text-3xl font-extrabold uppercase text-white">Contact Us</h2>
      {rows.length ? rows.map((r) => (
        <div key={r.label} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/70 p-4"><r.icon className="h-5 w-5 text-[#CCFF00]" /><div><p className="text-xs uppercase text-zinc-500">{r.label}</p><p className="text-white">{r.value}</p></div></div>
      )) : <p className="text-sm text-zinc-500">Contact details coming soon.</p>}
    </div>
  );
}

// ---- small helpers ----
function StepWrap({ title, onBack, children }) {
  return (
    <div className="px-5 py-6 space-y-4">
      <button data-testid="step-back-btn" onClick={onBack} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white"><ChevronLeft className="h-4 w-4" /> Back</button>
      <h2 className="font-heading text-3xl font-extrabold uppercase text-white">{title}</h2>
      {children}
    </div>
  );
}
function Field({ label, children }) { return <div><Label className="text-zinc-300">{label}</Label><div className="mt-1">{children}</div></div>; }
function Totals({ total }) {
  return <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-4 font-mono text-sm"><div className="flex justify-between text-white font-bold text-base"><span>Total</span><span className="text-[#CCFF00]">${total.toFixed(2)}</span></div></div>;
}
function Empty({ go }) {
  return <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-8 text-center"><ShoppingCart className="mx-auto h-10 w-10 text-zinc-600" /><p className="mt-3 text-zinc-400">Your cart is empty.</p><Button data-testid="cart-browse-btn" onClick={() => go("menu")} className="mt-4 bg-[#CCFF00] text-black font-bold hover:bg-[#d4ff33]">Browse Menu</Button></div>;
}
